/**
 * Calculator Reset Hook
 * Handles clearing drafts and resetting calculator state to defaults
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { draftService } from '@/services/draftService';
import { toast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { draftKeys } from '@/hooks/drafts/useDraftQuery';

export interface UseCalculatorResetOptions {
  formSlug: string;
  step?: string | null;
  onReset?: () => void;
}

export interface UseCalculatorResetReturn {
  resetCalculator: () => Promise<void>;
  isResetting: boolean;
}

export function useCalculatorReset(options: UseCalculatorResetOptions): UseCalculatorResetReturn {
  const { formSlug, step = null, onReset } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isResetting, setIsResetting] = useState(false);

  const resetCalculator = useCallback(async () => {
    setIsResetting(true);

    try {
      // Clear draft from database if user is logged in
      if (user?.id) {
        await draftService.deleteDraft({
          userId: user.id,
          formSlug,
          step,
        });

        // Invalidate draft queries
        queryClient.invalidateQueries({
          queryKey: draftKeys.user(user.id),
        });
      }

      // Call the reset callback to reset local state
      onReset?.();

      toast({
        title: 'Calculator reset',
        description: 'All values have been reset to defaults.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to reset calculator:', error);
      toast({
        title: 'Reset failed',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setIsResetting(false);
    }
  }, [user?.id, formSlug, step, onReset, queryClient]);

  return {
    resetCalculator,
    isResetting,
  };
}
